import { atom } from 'nanostores';
import { SHOW_PRICING_OVERLAY_PARAM } from './config';
import type { PricingCardActionId } from './types';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent, PricingPageOpenedTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import type { SubscriptionInterval } from '~/types/subscription';
import { hmrPropertyFactory } from '~/utils/hmr';
import type { Pricing } from '~/utils/pricing';

type PricingType = 'free' | 'pro' | 'teams';

const hmr = hmrPropertyFactory('PricingStore');

export class PricingStore {
  @hmr()
  showPricingOverlay = atom<boolean>(false);

  openPricingOverlay = (trigger: PricingPageOpenedTrigger) => {
    const url = new URL(window.location.href);
    url.searchParams.set(SHOW_PRICING_OVERLAY_PARAM, 'true');
    window.history.replaceState({}, '', url);

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.PricingPageOpened,
        properties: {
          trigger,
        },
      },
      isAnonymous: true,
    });

    this.showPricingOverlay.set(true);
  };

  closePricingOverlay = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(SHOW_PRICING_OVERLAY_PARAM);
    window.history.replaceState({}, '', url);

    this.showPricingOverlay.set(false);
    this.updatePlanScreenData.set(null);
    this.showDowngradeModalData.set(null);
  };

  @hmr()
  authDialogOpen = atom<boolean>(false);

  @hmr()
  showDowngradeModalData = atom<{ isPersonal: boolean; organization?: OrganizationData } | null>(null);

  @hmr()
  isLoading = atom<boolean>(false);

  @hmr()
  pricingBeingRequested = atom<{
    type: PricingType;
    id: PricingCardActionId;
  } | null>(null);

  @hmr()
  selectedInterval = atom<SubscriptionInterval>('monthly');

  @hmr()
  updatePlanScreenData = atom<{
    updateType: 'upgrade' | 'downgrade' | 'change_interval';
    pricing: Pricing;
    interval: SubscriptionInterval;
    organization?: OrganizationData;
  } | null>(null);

  startRequestForPricing(type: PricingType, id: PricingCardActionId) {
    this.isLoading.set(true);

    if (id !== 'cancel-subscription') {
      // we don't need spinner for cancel-subscription action on pricing card because it is managed from separate modal
      this.pricingBeingRequested.set({ type, id });
    }
  }

  endRequestForPricing() {
    this.pricingBeingRequested.set(null);
    this.isLoading.set(false);
  }
}

export const pricingStore = new PricingStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { pricingStore });
}
